import { z } from "zod"
import { emailSchema } from "../common/email-validator"
import { phoneNumberSchema } from "../common/phone-number-validator"
import { TranslationKey } from "../i18n/translation-key"
import { JobStatus } from "../service/job"

/** Create/update body for a store's customer, entered by store staff. */
export const SaveStoreCustomerSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "common.validation.firstNameRequired" satisfies TranslationKey),
  lastName: z
    .string()
    .trim()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  phoneNumber: phoneNumberSchema(),
  email: emailSchema().nullable(),
})
export type SaveStoreCustomer = z.infer<typeof SaveStoreCustomerSchema>

export enum StoreCustomerSortBy {
  NAME = "NAME",
  NUM_JOBS = "NUM_JOBS",
  LATEST_JOB_DATE = "LATEST_JOB_DATE",
  TOTAL_SPENT = "TOTAL_SPENT",
  CREATED_AT = "CREATED_AT",
}

export const StoreCustomerListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  size: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().trim().optional(),
  sortBy: z.nativeEnum(StoreCustomerSortBy).default(StoreCustomerSortBy.NAME),
  sortOrder: z.enum(["asc", "desc"]).default("asc"),
})
export type StoreCustomerListQuery = z.infer<
  typeof StoreCustomerListQuerySchema
>

export const StoreCustomerSearchQuerySchema = z.object({
  query: z.string().trim().min(1),
})
export type StoreCustomerSearchQuery = z.infer<
  typeof StoreCustomerSearchQuerySchema
>

export interface StoreCustomerContact {
  id: string
  firstName: string
  lastName: string | null
  phoneNumber: string
  email: string | null
}

export interface StoreCustomerListItem extends StoreCustomerContact {
  numJobs: number
  latestJobDate: Date | null
  /** Sum of the jobs' order values, in kr. */
  totalSpent: number
  isClubMember: boolean
  latestMessageDate: Date | null
  createdAt: Date
}

export interface StoreCustomerList {
  customers: StoreCustomerListItem[]
  totalPages: number
  totalCount: number
}

export interface StoreCustomerJob {
  id: string
  displayNumber: number
  status: JobStatus
  createdAt: Date
  handInDate: Date
  totalOrderValue: number | null
  bikeDescription: string | null
  /** The customer's bike the job was for, once the job has one. */
  storeBikeId: string | null
}

export interface StoreCustomerDetail extends StoreCustomerListItem {
  jobs: StoreCustomerJob[]
}

/** What the customer sees on their own page in the marketplace. */
export interface StoreCustomerAsCustomer {
  id: string
  firstName: string
  store: {
    id: string
    name: string
    slug: string
  }
  jobs: StoreCustomerJob[]
}

export interface StoreCustomerAlreadyExists {
  existingStoreCustomerId: string
}
